import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { useEffect } from "react"
import { useAutoTranslation } from "@/entities"
import { LANG_KEYS } from "@/shared/constants"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 transition-all duration-200",
  {
    variants: {
      variant: {
        default:
          "bg-card text-card-foreground border-border [&>svg]:text-foreground",
        destructive:
          "bg-destructive/10 text-destructive border-destructive/30 [&>svg]:text-destructive *:data-[slot=alert-description]:text-destructive/80",
        success:
          "bg-success/10 text-success border-success/30 [&>svg]:text-success *:data-[slot=alert-description]:text-success/80",
        warning:
          "bg-warning/10 text-warning-foreground border-warning/30 [&>svg]:text-warning *:data-[slot=alert-description]:text-warning-foreground/80",
        info: "bg-info/10 text-info border-info/30 [&>svg]:text-info *:data-[slot=alert-description]:text-info/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface AlertProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof alertVariants> {
  autoCloseTimeout?: number
  onClose?: () => void
  WrapperNode?: React.ComponentType<{ children: React.ReactNode }>
}

function Alert({
  className,
  variant,
  autoCloseTimeout,
  onClose,
  WrapperNode,
  ...props
}: AlertProps) {
  const { t } = useAutoTranslation()
  // Auto-close functionality
  useEffect(() => {
    if (autoCloseTimeout && onClose) {
      const timer = setTimeout(() => {
        onClose()
      }, autoCloseTimeout)

      return () => clearTimeout(timer)
    }
  }, [autoCloseTimeout, onClose])

  const content = (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={t(LANG_KEYS.CLOSE)}
          className="hover:bg-accent focus-visible:ring-ring absolute top-2 right-2 flex cursor-pointer items-center justify-center rounded-full p-1 text-xl transition-colors focus:outline-none focus-visible:ring-2"
          tabIndex={0}
        >
          {/* SVG X Icon */}
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            className="h-4 w-4"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="8" cy="8" r="7" stroke="currentColor" />
            <path d="M6 6l4 4M10 6l-4 4" stroke="currentColor" />
          </svg>
        </button>
      )}
      {props.children}
    </div>
  )

  return WrapperNode ? <WrapperNode>{content}</WrapperNode> : content
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, alertVariants }
